import { useCallback } from "react";

import { useQueryParams } from "../../core/routes/hooks";
import { SearchParamKeys, useSearchParams } from "../../shared/hooks";
import { WEB_LENS_CREATE_MODE, WEB_LENS_MODE_PARAM, type WebLensStartMode, isWebLensStartMode } from "../model";

export interface WebLensStartModeState {
    /** Which of the chooser's forks is active: create (default), update, or push-to-device. */
    mode: WebLensStartMode;
    setMode: (mode: WebLensStartMode) => void;
}

/** An absent or unrecognized ?mode= reads as create, so old and mistyped links keep working. */
const parseWebLensStartMode = (value: string | null): WebLensStartMode =>
    value !== null && isWebLensStartMode(value) ? value : WEB_LENS_CREATE_MODE;

/**
 * The start page's create/update/push fork, carried in the URL (?mode=update / ?mode=push) so it
 * survives reloads and can be linked.
 *
 * setQueryParams rather than updateQueryParams because returning to create must remove the param;
 * the folder choice is the only other param carried across the fork.
 */
export const useWebLensStartMode = (): WebLensStartModeState => {
    const searchParams = useSearchParams();
    const { setQueryParams } = useQueryParams();

    const mode = parseWebLensStartMode(searchParams.get(WEB_LENS_MODE_PARAM));

    const setMode = useCallback(
        (nextMode: WebLensStartMode) => {
            const params: Record<string, string> = {};
            const folderId = searchParams.get(SearchParamKeys.LensFolder);
            if (folderId) {
                params[SearchParamKeys.LensFolder] = folderId;
            }
            if (nextMode !== WEB_LENS_CREATE_MODE) {
                params[WEB_LENS_MODE_PARAM] = nextMode;
            }
            setQueryParams(params);
        },
        [searchParams, setQueryParams],
    );

    return { mode, setMode };
};
